/**
 * wayai analytics — show analytics summary and metrics for the current hub
 *
 * Usage:
 *   wayai analytics                                      # summary for last 7d
 *   wayai analytics --period 30d                         # summary for last 30 days
 *   wayai analytics --from 2026-01-01 --to 2026-01-31    # explicit date range
 *   wayai analytics --metric tokens_total,first_response_time
 *   wayai analytics --filter "tokens_total gt 5000"      # repeat for AND'd filters
 *   wayai analytics --filter "user_sentiment is positive"
 *   wayai analytics --list-metrics                       # list available metrics
 *   wayai analytics --json                               # raw JSON output
 *
 *   wayai analytics query \
 *     --metric "col=data.system.tokens_total,agg=avg,alias=avg_tokens" \
 *     --metric "col=data.system.tokens_total,agg=corr,corr_with=data.system.avg_agent_response_time,alias=tok_resp" \
 *     --group-by data.meta.kanban_status \
 *     --filter "data.system.tokens_total gt 0" \
 *     --order-by avg_tokens:desc --limit 50 --from 2026-01-01 --to 2026-01-31
 *
 * `query` is a thin wrapper over POST /api/analytics/query — supports aggregations
 * (avg|sum|min|max|count|quantile|stddevPop|corr|uniq), group_by, order_by, raw
 * column filters (eq|neq|gt|gte|lt|lte|in|not_in|like) and correlation via corr_with.
 *
 *   wayai analytics sql "SELECT count() FROM conversation"
 *   wayai analytics sql "SELECT toString(data.meta.kanban_status), count() FROM conversation GROUP BY 1" --json
 *   wayai analytics sql "SELECT agent_model, sum(toFloat64OrNull(toString(data.system.cost_usd_total))) FROM message WHERE created_at >= now() - INTERVAL 30 DAY GROUP BY 1"
 *   wayai analytics sql --schema                                  # column + variable-path catalog
 *   wayai analytics sql "..." --limit 500
 *
 * `sql` is a thin wrapper over POST /api/analytics/sql — server enforces hub_id +
 * is_eval=false scoping (a CTE shadow per readable table), single-SELECT only,
 * function/keyword denylist, 10k row cap. Two tables are readable: `conversation`
 * (one row per conversation) and `message` (per-message tokens/cost/operations).
 * `--schema` calls GET /api/analytics/sql/schema for both.
 *
 * `data.*` paths are JSON sub-paths typed `Dynamic`; aggregates and GROUP BY both
 * reject them uncast. Wrap numerics in `toFloat64OrNull(toString(...))` and grouping
 * keys in `toString(...)` — see `messageGrainNotes()`.
 */

import { requireAuth } from '../lib/auth.js';
import { requireRepoConfig } from '../lib/repo-config.js';
import { resolveActiveHubId } from '../lib/workspace.js';
import { ApiClient } from '../lib/api-client.js';
import { parsePeriod, parseDateArg, parsePositiveIntFlag } from '../lib/utils.js';
import { printQueryRows, printSqlSchema } from '../lib/sql-output.js';
import {
  NUMERIC_FILTER_OPS,
  TEXT_FILTER_OPS,
  CATEGORICAL_FILTER_OPS,
  VARIABLE_FILTER_OPS,
  STRUCTURED_QUERY_OPS,
  STRUCTURED_QUERY_AGGREGATIONS,
} from '../lib/analytics-constants.js';

type VariableType = 'numeric' | 'categorical' | 'text';
type StructuredAggregation = (typeof STRUCTURED_QUERY_AGGREGATIONS)[number];
type StructuredOp = (typeof STRUCTURED_QUERY_OPS)[number];

interface AnalyticsVariable {
  variable_id: string;
  variable_name: string;
  variable_type: VariableType;
}

const NUMERIC_OPS = new Set<string>(NUMERIC_FILTER_OPS);
const TEXT_OPS = new Set<string>(TEXT_FILTER_OPS);
const CATEGORICAL_OPS = new Set<string>(CATEGORICAL_FILTER_OPS);
const ALL_DATA_OPS = new Set<string>(VARIABLE_FILTER_OPS);
const STRUCTURED_OPS = new Set<string>(STRUCTURED_QUERY_OPS);
const STRUCTURED_AGGREGATIONS = new Set<string>(STRUCTURED_QUERY_AGGREGATIONS);

export async function analyticsCommand(args: string[]): Promise<void> {
  if (args[0] === 'query') {
    return analyticsQuerySubcommand(args.slice(1));
  }
  if (args[0] === 'sql') {
    return analyticsSqlSubcommand(args.slice(1));
  }
  return analyticsSummaryCommand(args);
}

// ---------------------------------------------------------------------------
// Default summary command (POST /api/analytics/data)
// ---------------------------------------------------------------------------

async function analyticsSummaryCommand(args: string[]): Promise<void> {
  const hubId = resolveActiveHubId(args);
  const { config, accessToken } = await requireAuth();
  requireRepoConfig();

  let period: string | undefined;
  let from: string | undefined;
  let to: string | undefined;
  let metricNames: string[] | undefined;
  const filterArgs: string[] = [];
  let listMetrics = false;
  let jsonOutput = false;

  for (let i = 0; i < args.length; i++) {
    const arg = args[i];
    if (arg === '--period') {
      if (i + 1 >= args.length) { console.error('--period requires a value'); process.exit(1); }
      period = args[++i];
    } else if (arg === '--from') {
      if (i + 1 >= args.length) { console.error('--from requires a value'); process.exit(1); }
      from = args[++i];
    } else if (arg === '--to') {
      if (i + 1 >= args.length) { console.error('--to requires a value'); process.exit(1); }
      to = args[++i];
    } else if (arg === '--metric') {
      if (i + 1 >= args.length) { console.error('--metric requires a value'); process.exit(1); }
      metricNames = args[++i].split(',').map((s) => s.trim()).filter(Boolean);
    } else if (arg === '--filter') {
      if (i + 1 >= args.length) { console.error('--filter requires a value'); process.exit(1); }
      filterArgs.push(args[++i]);
    } else if (arg === '--list-metrics') {
      listMetrics = true;
    } else if (arg === '--json') {
      jsonOutput = true;
    }
  }

  const client = new ApiClient({ apiUrl: config.api_url, accessToken });

  if (listMetrics) {
    const result = await client.getAnalyticsVariables(hubId);
    if (jsonOutput) {
      console.log(JSON.stringify(result.data, null, 2));
      return;
    }
    printVariablesByCategory(result.data);
    return;
  }

  if ((from && !to) || (!from && to)) {
    console.error('--from and --to must both be provided together.');
    process.exit(1);
  }

  let start: string;
  let end: string;
  let periodicity: string;

  try {
    if (from && to) {
      start = parseDateArg(from, 'from');
      end = parseDateArg(to, 'to');
      if (new Date(start).getTime() >= new Date(end).getTime()) {
        console.error('--from must be before --to.');
        process.exit(1);
      }
      const rangeMs = new Date(end).getTime() - new Date(start).getTime();
      const rangeDays = rangeMs / (1000 * 60 * 60 * 24);
      if (rangeDays > 365) periodicity = 'yearly';
      else if (rangeDays > 90) periodicity = 'monthly';
      else if (rangeDays > 14) periodicity = 'weekly';
      else periodicity = 'daily';
    } else {
      const parsed = parsePeriod(period || '7d');
      start = parsed.start;
      end = parsed.end;
      periodicity = parsed.periodicity;
    }
  } catch (err) {
    console.error(err instanceof Error ? err.message : String(err));
    process.exit(1);
  }

  // Resolve --metric and --filter against the variable catalog (single fetch).
  let allVars: AnalyticsVariable[] | undefined;
  let variableNameMap: Map<string, string> | undefined;

  const needsCatalog = (metricNames && metricNames.length > 0) || filterArgs.length > 0;
  if (needsCatalog) {
    const varsResult = await client.getAnalyticsVariables(hubId);
    allVars = Object.values(varsResult.data.variablesByCategory).flat();
    variableNameMap = new Map(allVars.map((v) => [v.variable_id, v.variable_name]));
  }

  let selectedVariables: string[] = [];
  if (metricNames && metricNames.length > 0 && allVars) {
    const notFound: string[] = [];
    for (const name of metricNames) {
      const match = allVars.find((v) => v.variable_name === name);
      if (match) selectedVariables.push(match.variable_id);
      else notFound.push(name);
    }
    if (notFound.length > 0) {
      const available = allVars.map((v) => v.variable_name).sort();
      console.error(`Unknown metric(s): ${notFound.join(', ')}`);
      console.error(`\nAvailable metrics:\n  ${available.join('\n  ')}`);
      process.exit(1);
    }
  }

  let parsedFilters: Array<{ variable_id: string; filter_type: string; filter_value: string | number }> | undefined;
  if (filterArgs.length > 0 && allVars) {
    parsedFilters = filterArgs.map((raw) => parseFilterByName(raw, allVars!));
  }

  const result = await client.getAnalyticsData({
    hub_id: hubId,
    selected_variables: selectedVariables,
    time_interval: { start, end },
    periodicity,
    trend_analysis: false,
    include_summary: true,
    ...(parsedFilters && { filters: parsedFilters }),
  });

  if (jsonOutput) {
    console.log(JSON.stringify(result, null, 2));
    return;
  }

  const label = from && to ? `${from} to ${to}` : `last ${period || '7d'}`;
  console.log(`Hub Analytics (${label})\n`);

  if (result.summary) {
    const s = result.summary;
    console.log('Summary:');
    console.log(`  Total conversations:     ${s.total_conversations}`);
    console.log(`  AI-only rate:            ${(s.ai_only_rate * 100).toFixed(1)}%`);
    console.log(`  Avg response time:       ${formatMinutes(s.avg_response_time)}`);
    console.log(`  AI response time:        ${formatMinutes(s.assistant_response_time)}`);
    console.log(`  Support response time:   ${formatMinutes(s.support_response_time)}`);
  }

  if (result.analytics && result.analytics.length > 0) {
    console.log('\nMetrics:');
    for (const item of result.analytics) {
      const name = variableNameMap?.get(item.variable_id) || item.variable;
      console.log(`  ${name}:`);
      if (item.type === 'numeric' && item.metrics) {
        const m = item.metrics;
        const parts: string[] = [];
        parts.push(`Average: ${fmt(m.average)}`);
        if (m.median !== undefined) parts.push(`Median: ${fmt(m.median)}`);
        parts.push(`Min: ${fmt(m.minimum)}`);
        parts.push(`Max: ${fmt(m.maximum)}`);
        if (m.count !== undefined) parts.push(`Count: ${m.count}`);
        console.log(`    ${parts.join('   ')}`);
      } else if (item.type === 'categorical' && item.distribution) {
        for (const [cat, count] of Object.entries(item.distribution)) {
          console.log(`    ${cat}: ${count}`);
        }
      }
    }
  }
}

// ---------------------------------------------------------------------------
// `wayai analytics query` — POST /api/analytics/query (structured)
// ---------------------------------------------------------------------------

async function analyticsQuerySubcommand(args: string[]): Promise<void> {
  const hubId = resolveActiveHubId(args);
  const { config, accessToken } = await requireAuth();
  requireRepoConfig();

  const metricArgs: string[] = [];
  const orderByArgs: string[] = [];
  const filterArgs: string[] = [];
  let groupByRaw: string | undefined;
  let from: string | undefined;
  let to: string | undefined;
  let limit: number | undefined;
  let jsonOutput = false;

  for (let i = 0; i < args.length; i++) {
    const arg = args[i];
    if (arg === '--metric') {
      if (i + 1 >= args.length) { console.error('--metric requires a value'); process.exit(1); }
      metricArgs.push(args[++i]);
    } else if (arg === '--group-by') {
      if (i + 1 >= args.length) { console.error('--group-by requires a value'); process.exit(1); }
      groupByRaw = args[++i];
    } else if (arg === '--order-by') {
      if (i + 1 >= args.length) { console.error('--order-by requires a value'); process.exit(1); }
      orderByArgs.push(args[++i]);
    } else if (arg === '--filter') {
      if (i + 1 >= args.length) { console.error('--filter requires a value'); process.exit(1); }
      filterArgs.push(args[++i]);
    } else if (arg === '--from') {
      if (i + 1 >= args.length) { console.error('--from requires a value'); process.exit(1); }
      from = args[++i];
    } else if (arg === '--to') {
      if (i + 1 >= args.length) { console.error('--to requires a value'); process.exit(1); }
      to = args[++i];
    } else if (arg === '--limit') {
      limit = parsePositiveIntFlag('--limit', args[++i]);
    } else if (arg === '--json') {
      jsonOutput = true;
    } else {
      console.error(`Unknown flag for \`analytics query\`: ${arg}`);
      process.exit(1);
    }
  }

  if (metricArgs.length === 0) {
    console.error('At least one --metric is required for `analytics query`.');
    console.error('Example: --metric "col=data.system.tokens_total,agg=avg,alias=avg_tokens"');
    process.exit(1);
  }

  if ((from && !to) || (!from && to)) {
    console.error('--from and --to must both be provided together.');
    process.exit(1);
  }

  let timeRange: { start: string; end: string } | undefined;
  if (from && to) {
    try {
      const start = parseDateArg(from, 'from');
      const end = parseDateArg(to, 'to');
      if (new Date(start).getTime() >= new Date(end).getTime()) {
        console.error('--from must be before --to.');
        process.exit(1);
      }
      timeRange = { start, end };
    } catch (err) {
      console.error(err instanceof Error ? err.message : String(err));
      process.exit(1);
    }
  }

  const metrics = metricArgs.map(parseMetricSpec);
  const filters = filterArgs.map(parseStructuredFilter);
  const orderBy = orderByArgs.map(parseOrderBy);
  const groupBy = groupByRaw ? groupByRaw.split(',').map((s) => s.trim()).filter(Boolean) : undefined;

  const client = new ApiClient({ apiUrl: config.api_url, accessToken });

  const result = await client.runAnalyticsQuery({
    hub_id: hubId,
    metrics,
    ...(groupBy && groupBy.length > 0 && { group_by: groupBy }),
    ...(orderBy.length > 0 && { order_by: orderBy }),
    ...(filters.length > 0 && { filters }),
    ...(timeRange && { time_range: timeRange }),
    ...(limit !== undefined && { limit }),
  });

  if (jsonOutput) {
    console.log(JSON.stringify(result, null, 2));
    return;
  }

  printQueryRows(result.data);
}

// ---------------------------------------------------------------------------
// `wayai analytics sql` — POST /api/analytics/sql (read-only, server-scoped)
// ---------------------------------------------------------------------------

async function analyticsSqlSubcommand(args: string[]): Promise<void> {
  const hubId = resolveActiveHubId(args);
  const { config, accessToken } = await requireAuth();
  requireRepoConfig();

  let sql: string | undefined;
  let limit: number | undefined;
  let jsonOutput = false;
  let schemaOnly = false;

  for (let i = 0; i < args.length; i++) {
    const arg = args[i];
    if (arg === '--limit') {
      limit = parsePositiveIntFlag('--limit', args[++i]);
    } else if (arg === '--json') {
      jsonOutput = true;
    } else if (arg === '--schema') {
      schemaOnly = true;
    } else if (!arg.startsWith('--') && sql === undefined) {
      sql = arg;
    } else {
      console.error(`Unknown flag for \`analytics sql\`: ${arg}`);
      process.exit(1);
    }
  }

  const client = new ApiClient({ apiUrl: config.api_url, accessToken });

  if (schemaOnly) {
    const [conversation, message] = await Promise.all([
      client.getAnalyticsSqlSchema(hubId, 'conversation'),
      client.getAnalyticsSqlSchema(hubId, 'message'),
    ]);
    const notes = messageGrainNotes();
    if (jsonOutput) {
      console.log(JSON.stringify({
        conversation: conversation.data,
        message: { ...message.data, notes },
      }, null, 2));
      return;
    }
    printSqlSchema(conversation.data, 'Variable paths (data.system.* + data.variables.* + data.annotations.*):');
    console.log('');
    printSqlSchema(message.data, 'Variable paths (data.system.* — per-message usage facts):');
    console.log('');
    for (const line of notes) console.log(line);
    return;
  }

  if (!sql) {
    console.error('Provide a SQL string. Example: wayai analytics sql "SELECT count() FROM conversation"');
    console.error('Cost example: wayai analytics sql "SELECT agent_model, sum(toFloat64OrNull(toString(data.system.cost_usd_total))) AS spend FROM message WHERE created_at >= now() - INTERVAL 30 DAY GROUP BY agent_model"');
    console.error('Or run: wayai analytics sql --schema   to list available columns and paths (data.* paths need a cast).');
    process.exit(1);
  }

  const result = await client.runAnalyticsSql({
    hub_id: hubId,
    sql,
    ...(limit !== undefined && { limit }),
  });

  if (jsonOutput) {
    console.log(JSON.stringify(result, null, 2));
    return;
  }

  printQueryRows(result.data);
}

/**
 * What a reader must know before the first `message` query: one rule that decides
 * whether it RUNS, the rest whether its numbers are right and what they cost.
 *
 * Returned as lines rather than printed so the `--json` consumer — the one an agent
 * actually parses — carries them too. The knowledge is useless on the path nobody
 * reads, which is also why this must stay the FULL rule set: an agent whose only
 * source is `--schema --json` sees these lines and nothing else.
 *
 * Mirrors `## Raw SQL & Cost Analysis` in skills/wayai/references/analytics.md.
 */
export function messageGrainNotes(): string[] {
  return [
    'Reading `message` (per-message usage facts):',
    '  - CAST every data.* path or the query fails: JSON paths resolve to Dynamic,',
    '    which aggregates and GROUP BY both reject.',
    '      numeric:  sum(toFloat64OrNull(toString(data.system.cost_usd_total)))',
    '      grouping: toString(c.data.meta.kanban_status)   -- a CONVERSATION path',
    '    A `message` row carries data.system.* ONLY — no meta, variables, or',
    '    eval_scores. Selecting data.meta.* from `message` returns NULL rather than',
    '    erroring, so it collapses to one empty group; reach it by joining',
    '    `conversation`. Top-level columns (agent_model, agent_role, connection_id,',
    '    created_at) are typed already and need no cast.',
    '  - Rows are written when a conversation CLOSES, best-effort. An open',
    '    conversation has no rows, and `message` is less durable than',
    '    `conversation` — treat it as analysis-grade, not billing-grade.',
    '  - An unfiltered sum EQUALS the matching conversation figure: `message` is the',
    '    DECOMPOSITION of that figure, every agent role included. Use the filter below',
    '    only to ISOLATE the spend a hub\'s own agents did, splitting it from platform',
    '    background work (evaluators, monitor, summarizer). It lands at or below the',
    '    conversation total — `filtered <= total`, strict only when an excluded row',
    '    actually contributed to the metric you selected. A conversation that ran no',
    '    background agent has nothing to exclude, so equality there is correct, not a',
    '    fault. Filter NULL-safely, KEEPING the outer parentheses (AND binds tighter',
    '    than OR, so an unparenthesized copy combined with any other condition silently',
    '    readmits every NULL-role row):',
    "      WHERE (agent_role IS NULL OR agent_role NOT IN ('conversation_evaluator','message_evaluator','monitor','summarizer'))",
    '  - HISTORICAL BOUNDARIES — TWO, both forward-only and neither backfilled, so a',
    '    conversation reflects whichever rules were live when it CLOSED:',
    '      * `summarizer` spend entered BOTH tables when the provider-usage carrier',
    '        shipped. Before that it was recorded nowhere; no query recovers it.',
    '      * evaluator and monitor spend entered the `conversation` aggregate later,',
    '        when the role exclusion narrowed to shape only. `message` carried those',
    '        rows all along, so for a window spanning only that second change, summing',
    '        `message` is the continuous answer.',
    '  - `connection_id` names the LLM connection only. TTS/STT/container spend',
    '    is folded into the same row, so attributing cost to a credential means',
    '    netting out cost_usd_tts / cost_usd_stt / cost_usd_container.',
    '  - BUCKET DATES IN THE HUB TIMEZONE, not raw UTC:',
    "      toDate(toTimeZone(created_at, 'America/Sao_Paulo'))",
    '    Raw UTC bucketing misassigns every row near hub midnight.',
    '  - BOUND created_at. The table retains five years at per-message volume, so',
    '    an unbounded scan is the expensive way to ask any question:',
    '      WHERE created_at >= now() - INTERVAL 30 DAY',
    '  - The conversation figure stays the customer-visible one, and it is now the',
    '    TOTAL: every role that spent on the conversation is in it.',
  ];
}

// ---------------------------------------------------------------------------
// Parsing helpers
// ---------------------------------------------------------------------------

/**
 * Split a "<lhs> <op> <value...>" filter expression. Value may contain spaces
 * (e.g. "feedback contains good service"). Exits the process on malformed input.
 */
function splitFilterExpr(raw: string, shape: string): { lhs: string; op: string; value: string } {
  const parts = raw.trim().split(/\s+/);
  if (parts.length < 3) {
    console.error(`Invalid --filter "${raw}". Expected: "${shape}".`);
    process.exit(1);
  }
  const [lhs, op, ...rest] = parts;
  return { lhs, op, value: rest.join(' ') };
}

/**
 * Parse `--filter "name op value"` for the variable-aware /data endpoint.
 * `name` resolves to a variable_id; `op` is validated against the variable's type.
 */
function parseFilterByName(raw: string, allVars: AnalyticsVariable[]): {
  variable_id: string; filter_type: string; filter_value: string | number;
} {
  const { lhs: name, op, value } = splitFilterExpr(raw, '<name> <op> <value>');

  const variable = allVars.find((v) => v.variable_name === name);
  if (!variable) {
    const available = allVars.map((v) => v.variable_name).sort();
    console.error(`Unknown variable in --filter: ${name}`);
    console.error(`\nAvailable variables:\n  ${available.join('\n  ')}`);
    process.exit(1);
  }

  if (!ALL_DATA_OPS.has(op)) {
    console.error(`Invalid filter operator: ${op}`);
    console.error('Numeric ops: eq, neq, gt, gte, lt, lte');
    console.error('Text ops:    equals, not_equals, contains, not_contains, starts_with, ends_with');
    console.error('Categorical ops: is, is_not, in, not_in');
    process.exit(1);
  }

  const expectedSet =
    variable.variable_type === 'numeric' ? NUMERIC_OPS :
    variable.variable_type === 'text' ? TEXT_OPS :
    CATEGORICAL_OPS;
  if (!expectedSet.has(op)) {
    console.error(`Operator "${op}" is not valid for ${variable.variable_type} variable "${name}".`);
    console.error(`Valid operators: ${[...expectedSet].join(', ')}`);
    process.exit(1);
  }

  let filterValue: string | number;
  if (variable.variable_type === 'numeric') {
    const n = Number(value);
    if (!Number.isFinite(n)) {
      console.error(`Numeric filter value must be a number, got: "${value}"`);
      process.exit(1);
    }
    filterValue = n;
  } else {
    filterValue = value;
  }

  return { variable_id: variable.variable_id, filter_type: op, filter_value: filterValue };
}

/**
 * Parse `--metric "col=...,agg=...[,alias=...][,corr_with=...][,level=...]"`.
 * Order doesn't matter; unknown keys are rejected so typos surface immediately.
 */
function parseMetricSpec(raw: string): {
  column: string; aggregation: StructuredAggregation; alias?: string; corr_with?: string; quantile_level?: number;
} {
  const fields = raw.split(',').map((s) => s.trim()).filter(Boolean);
  const map = new Map<string, string>();
  for (const field of fields) {
    const eq = field.indexOf('=');
    if (eq < 0) {
      console.error(`Invalid --metric segment "${field}". Expected key=value pairs.`);
      process.exit(1);
    }
    const key = field.slice(0, eq).trim();
    const val = field.slice(eq + 1).trim();
    if (map.has(key)) {
      console.error(`Duplicate key "${key}" in --metric "${raw}".`);
      process.exit(1);
    }
    map.set(key, val);
  }

  const knownKeys = new Set(['col', 'agg', 'alias', 'corr_with', 'level']);
  for (const key of map.keys()) {
    if (!knownKeys.has(key)) {
      console.error(`Unknown --metric key: "${key}". Allowed: ${[...knownKeys].join(', ')}`);
      process.exit(1);
    }
  }

  const column = map.get('col');
  const aggregation = map.get('agg');
  if (!column || !aggregation) {
    console.error(`--metric requires col and agg, got: "${raw}"`);
    process.exit(1);
  }
  if (!STRUCTURED_AGGREGATIONS.has(aggregation)) {
    console.error(`Invalid aggregation: "${aggregation}". Allowed: ${[...STRUCTURED_AGGREGATIONS].join(', ')}`);
    process.exit(1);
  }
  if (aggregation === 'corr' && !map.has('corr_with')) {
    console.error(`--metric agg=corr requires corr_with=<column>.`);
    process.exit(1);
  }

  const spec: { column: string; aggregation: StructuredAggregation; alias?: string; corr_with?: string; quantile_level?: number } = {
    column, aggregation: aggregation as StructuredAggregation,
  };
  if (map.has('alias')) spec.alias = map.get('alias');
  if (map.has('corr_with')) spec.corr_with = map.get('corr_with');
  if (map.has('level')) {
    const lvl = Number(map.get('level'));
    if (!Number.isFinite(lvl) || lvl < 0 || lvl > 1) {
      console.error(`--metric level must be a number between 0 and 1, got "${map.get('level')}"`);
      process.exit(1);
    }
    spec.quantile_level = lvl;
  }
  return spec;
}

/**
 * Parse `--filter "<col> <op> <value>"` for the structured /query endpoint.
 * Columns are raw paths/identifiers — no name lookup.
 */
function parseStructuredFilter(raw: string): { column: string; operator: StructuredOp; value: string | number } {
  const { lhs: column, op: operator, value } = splitFilterExpr(raw, '<column> <op> <value>');
  if (!STRUCTURED_OPS.has(operator)) {
    console.error(`Invalid operator "${operator}". Allowed: ${[...STRUCTURED_OPS].join(', ')}`);
    process.exit(1);
  }
  // Equality and ordering operators forward numeric values as numbers when the
  // input parses as one; falls back to string for non-numeric eq/neq targets.
  // `value !== ''` guards `Number('') === 0` from coercing missing input.
  const parsedNum = Number(value);
  const isOrderOp = operator === 'gt' || operator === 'gte' || operator === 'lt' || operator === 'lte';
  const isEqualityOp = operator === 'eq' || operator === 'neq';
  const isFiniteValue = value !== '' && Number.isFinite(parsedNum);
  if (isOrderOp && !isFiniteValue) {
    console.error(`Filter value for "${operator}" must be numeric, got: "${value}"`);
    process.exit(1);
  }
  const coerceToNumber = isOrderOp || (isEqualityOp && isFiniteValue);
  return {
    column,
    operator: operator as StructuredOp,
    value: coerceToNumber ? parsedNum : value,
  };
}

function parseOrderBy(raw: string): { column: string; direction: 'asc' | 'desc' } {
  const [column, dirRaw = 'asc'] = raw.split(':').map((s) => s.trim());
  if (!column) {
    console.error(`Invalid --order-by "${raw}". Expected: "<column>[:asc|desc]".`);
    process.exit(1);
  }
  const dir = dirRaw.toLowerCase();
  if (dir !== 'asc' && dir !== 'desc') {
    console.error(`Invalid --order-by direction "${dirRaw}". Use asc or desc.`);
    process.exit(1);
  }
  return { column, direction: dir as 'asc' | 'desc' };
}

// ---------------------------------------------------------------------------
// Output helpers
// ---------------------------------------------------------------------------

function printVariablesByCategory(data: {
  categories: Array<{ category: string; label: string; variableCount: number }>;
  variablesByCategory: Record<string, Array<{
    variable_id: string;
    variable_name: string;
    variable_description?: string;
    variable_type: string;
    variable_origin: string;
  }>>;
}): void {
  console.log('Analytics Variables\n');
  for (const category of data.categories) {
    const variables = data.variablesByCategory[category.category] || [];
    console.log(`${category.label} (${category.variableCount}):`);
    if (variables.length === 0) {
      console.log('  (none)\n');
      continue;
    }
    for (const v of variables) {
      const desc = v.variable_description ? ` — ${v.variable_description}` : '';
      console.log(`  ${v.variable_name} (${v.variable_type})${desc}`);
    }
    console.log('');
  }
}

function formatMinutes(minutes: number | null | undefined): string {
  if (minutes == null) return 'N/A';
  if (minutes < 1) return `${(minutes * 60).toFixed(1)}s`;
  return `${minutes.toFixed(1)}min`;
}

function fmt(n: number | null | undefined): string {
  if (n == null) return 'N/A';
  return Number.isInteger(n) ? String(n) : n.toFixed(2);
}
